import { get, set } from 'idb-keyval';
import { createId } from './eventIds';

const QUEUE_KEY = 'cxnft_funnel_event_queue_v1';
const MAX_EVENTS = 1500;
const MAX_PAYLOAD_BYTES = 2 * 1024 * 1024;
const FETCH_BATCH_MAX_EVENTS = 75;
const FETCH_BATCH_MAX_BYTES = 250 * 1024;
const BEACON_BATCH_MAX_EVENTS = 20;
const BEACON_BATCH_MAX_BYTES = 52 * 1024;
const PRIORITY_EVENT_ORDER = new Map([
  ['experiment_assignment', 0],
  ['telegram_cta_click', 1],
  ['outbound_telegram_intent', 2],
  ['landing_page_view', 3],
  ['funnel_view', 4],
  ['funnel_variant_view', 5],
]);

let hotQueue = [];
let initializedPromise = null;

function hasWindow() {
  return typeof window !== 'undefined';
}

function isDebugEnabled() {
  return process.env.REACT_APP_FUNNEL_TRACKING_DEBUG === 'true';
}

function getEndpoint() {
  return process.env.REACT_APP_FUNNEL_EVENT_ENDPOINT || '';
}

function estimateBytes(events) {
  try {
    return new Blob([JSON.stringify(events)]).size;
  } catch (error) {
    return JSON.stringify(events).length;
  }
}

function trimQueue(events) {
  const trimmed = events.slice(-MAX_EVENTS);

  while (trimmed.length > 0 && estimateBytes(trimmed) > MAX_PAYLOAD_BYTES) {
    trimmed.shift();
  }

  return trimmed;
}

async function persistQueue() {
  hotQueue = trimQueue(hotQueue);
  await set(QUEUE_KEY, hotQueue);
}

export function initializeEventQueue() {
  if (initializedPromise) {
    return initializedPromise;
  }

  initializedPromise = get(QUEUE_KEY)
    .then((storedEvents) => {
      hotQueue = Array.isArray(storedEvents) ? trimQueue(storedEvents) : [];
      return hotQueue;
    })
    .catch(() => {
      hotQueue = [];
      return hotQueue;
    });

  return initializedPromise;
}

export async function enqueueEvent(event) {
  await initializeEventQueue();
  hotQueue.push(event);
  await persistQueue();

  if (isDebugEnabled() && hasWindow()) {
    // Keep this behind the debug flag because pointer telemetry can be noisy.
    console.info('[funnel-tracking]', event.event_name, event);
  }

  return event;
}

export async function dumpQueuedEvents() {
  await initializeEventQueue();
  const eventCounts = hotQueue.reduce((counts, event) => {
    const name = event.event_name || 'unknown';
    counts[name] = (counts[name] || 0) + 1;
    return counts;
  }, {});

  return {
    events: [...hotQueue],
    event_count: hotQueue.length,
    payload_bytes: estimateBytes(hotQueue),
    event_counts: eventCounts,
  };
}

function createBatch(events) {
  const firstEvent = events[0] || {};

  return {
    batch_id: createId('batch'),
    sent_at: new Date().toISOString(),
    visitor_session_id: firstEvent.visitor_session_id || '',
    page_instance_id: firstEvent.page_instance_id || '',
    events,
  };
}

function orderEventsForFlush(events) {
  return [...events].sort((left, right) => {
    const leftPriority = PRIORITY_EVENT_ORDER.get(left.event_name) ?? 100;
    const rightPriority = PRIORITY_EVENT_ORDER.get(right.event_name) ?? 100;

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }

    return (left.event_time || 0) - (right.event_time || 0);
  });
}

function chunkEvents(events, { maxEvents, maxBytes }) {
  const chunks = [];
  let current = [];

  events.forEach((event) => {
    const next = [...current, event];
    const tooManyEvents = next.length > maxEvents;
    const tooLarge = current.length > 0 && estimateBytes(next) > maxBytes;

    if (tooManyEvents || tooLarge) {
      chunks.push(current);
      current = [event];
    } else {
      current = next;
    }
  });

  if (current.length > 0) {
    chunks.push(current);
  }

  return chunks;
}

async function flushWithFetch(endpoint, body, timeoutMs, { keepalive = false } = {}) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => {
        controller.abort();
      }, timeoutMs)
    : null;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive,
      signal: controller?.signal,
    });

    return response.ok;
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

function flushWithBeacon(endpoint, body) {
  if (!navigator.sendBeacon) {
    return false;
  }

  try {
    return navigator.sendBeacon(
      endpoint,
      new Blob([body], { type: 'application/json' })
    );
  } catch (error) {
    return false;
  }
}

export async function flushEventQueue({ timeoutMs = 900, preferBeacon = false } = {}) {
  await initializeEventQueue();

  const endpoint = getEndpoint();
  if (!endpoint || hotQueue.length === 0 || !hasWindow()) {
    return {
      sent: false,
      reason: !endpoint ? 'missing_endpoint' : 'empty_queue',
      event_count: hotQueue.length,
    };
  }

  const maxEvents = preferBeacon ? BEACON_BATCH_MAX_EVENTS : FETCH_BATCH_MAX_EVENTS;
  const maxBytes = preferBeacon ? BEACON_BATCH_MAX_BYTES : FETCH_BATCH_MAX_BYTES;
  const eventsToSend = orderEventsForFlush(hotQueue);
  const chunks = chunkEvents(eventsToSend, { maxEvents, maxBytes });
  const sentIds = new Set();
  let sentBatchCount = 0;
  let failedBatchCount = 0;

  for (const chunk of chunks) {
    const body = JSON.stringify(createBatch(chunk));
    const bodyBytes = new Blob([body]).size;
    let sent = false;

    if (preferBeacon && bodyBytes <= BEACON_BATCH_MAX_BYTES) {
      sent = flushWithBeacon(endpoint, body);
    }

    if (!sent && typeof fetch === 'function') {
      try {
        sent = await flushWithFetch(endpoint, body, timeoutMs, {
          keepalive: preferBeacon && bodyBytes <= BEACON_BATCH_MAX_BYTES,
        });
      } catch (error) {
        sent = false;
      }
    }

    if (!sent) {
      failedBatchCount += 1;
      break;
    }

    sentBatchCount += 1;
    chunk.forEach((event) => sentIds.add(event.event_id));
  }

  if (sentIds.size > 0) {
    hotQueue = hotQueue.filter((event) => !sentIds.has(event.event_id));
    await persistQueue();
  }

  return {
    sent: failedBatchCount === 0 && sentIds.size > 0,
    event_count: sentIds.size,
    attempted_event_count: eventsToSend.length,
    sent_batch_count: sentBatchCount,
    failed_batch_count: failedBatchCount,
    retained_event_count: hotQueue.length,
  };
}
